import {
  CalendarClock,
  CalendarPlus,
  ChartNoAxesCombined,
  ClipboardList,
  ContactRound,
  CreditCard,
  FilePenLine,
  House,
  Inbox,
  LayoutDashboard,
  ListChecks,
  ListTodo,
  PanelsTopLeft,
  Plus,
  ReceiptText,
  ScrollText,
  LibraryBig,
  Settings2,
  ShieldCheck,
  SlidersHorizontal,
  UserPlus,
  UsersRound,
} from 'lucide-react';
import type { ComponentType } from 'react';
import { clinicRouteAccess } from './routeAccess';

/**
 * App navigation model — the single source of truth for the sidebar/nav destinations and the
 * "New" quick actions. Extracted from the shells so the desktop shell, mobile shell, and the
 * global-search command builder all read the same list without importing each other.
 */

export interface ShellRoute {
  path: string;
  labelKey: string;
  icon: React.ReactNode;
  // Match the route exactly instead of as a path prefix. Needed for destinations that are a prefix
  // of a sibling nav item (e.g. `/appointments` must not stay highlighted on `/appointments/requests`)
  // and for `/dashboard`. Drill-down detail pages (e.g. `/patients/:id`) intentionally keep their
  // parent highlighted, so those routes leave `end` unset.
  end?: boolean;
  permissions?: readonly string[];
  feature?: string;
}

export interface NavSection {
  // Section header shown above the group. Omitted for the top-level overview group (Dashboard).
  titleKey?: string;
  routes: ShellRoute[];
  // When set, the section renders as a collapsible group: a toggle button (labelled by `titleKey`,
  // iconed by `groupIcon`) that expands/collapses its routes, which are indented under a hairline rail.
  collapsible?: boolean;
  groupIcon?: React.ReactNode;
}

// Grouped into a few plain-language buckets so non-technical clinic staff can scan the sidebar at a
// glance rather than reading one long flat list.
export const navSections: NavSection[] = [
  {
    routes: [
      { path: '/dashboard', labelKey: 'workspace.nav.dashboard', icon: <LayoutDashboard />, end: true },
    ],
  },
  {
    titleKey: 'workspace.sections.frontDesk',
    routes: [
      { path: '/appointments', labelKey: 'workspace.nav.appointments', icon: <CalendarClock />, end: true, permissions: ['queue.view'] },
      { path: '/appointments/requests', labelKey: 'workspace.nav.requests', icon: <Inbox />, permissions: ['queue.view'] },
      { path: '/queue', labelKey: 'workspace.nav.queue', icon: <ListChecks />, permissions: ['queue.view'] },
    ],
  },
  {
    titleKey: 'workspace.sections.clinical',
    routes: [
      { path: '/patients', labelKey: 'workspace.nav.patients', icon: <ContactRound />, permissions: clinicRouteAccess['/patients'].permissions },
      { path: '/prescriptions', labelKey: 'workspace.nav.prescriptions', icon: <FilePenLine />, permissions: ['prescriptions.print'] },
    ],
  },
  {
    titleKey: 'workspace.sections.business',
    routes: [
      { path: '/billing', labelKey: 'workspace.nav.billing', icon: <ReceiptText />, permissions: clinicRouteAccess['/billing'].permissions, feature: clinicRouteAccess['/billing'].feature },
      { path: '/reports', labelKey: 'workspace.nav.reports', icon: <ChartNoAxesCombined />, permissions: ['analytics.clinic.view', 'analytics.own.view', 'analytics.limited.view'], feature: 'reportsLevel' },
    ],
  },
  {
    titleKey: 'workspace.sections.admin',
    // Admin destinations are folded into a single collapsible group so the everyday clinical nav stays
    // short; owners expand it to reach staff, roles, subscription, audit, and clinic-wide settings.
    collapsible: true,
    groupIcon: <SlidersHorizontal />,
    routes: [
      { path: '/staff', labelKey: 'workspace.nav.staff', icon: <UsersRound />, end: true, permissions: clinicRouteAccess['/staff'].permissions, feature: clinicRouteAccess['/staff'].feature },
      { path: '/roles', labelKey: 'workspace.nav.roles', icon: <ShieldCheck />, permissions: clinicRouteAccess['/roles'].permissions, feature: clinicRouteAccess['/roles'].feature },
      { path: '/subscription', labelKey: 'workspace.nav.subscription', icon: <CreditCard />, permissions: ['clinic.settings.manage'] },
      { path: '/audit', labelKey: 'workspace.nav.audit', icon: <ScrollText />, permissions: ['clinic.audit.view'] },
      {
        path: '/prescription-configuration',
        labelKey: 'workspace.nav.prescriptionConfiguration',
        icon: <LibraryBig />,
        permissions: [
          'clinic.settings.manage',
          'prescriptions.library.own.manage',
          'prescriptions.library.clinic.view',
          'prescriptions.library.clinic.manage',
        ],
      },
      { path: '/settings', labelKey: 'workspace.nav.settings', icon: <Settings2 />, permissions: clinicRouteAccess['/settings'].permissions },
    ],
  },
];

/** How many middle slots the bottom tab bar has between the fixed Home (first) and More (last) tabs.
 * Shared by MobileShell (which fills them) and MobileMorePage (whose list is everything else) so the
 * two can never drift out of sync. */
export const MOBILE_BOTTOM_TAB_MIDDLE_CAPACITY = 3;

export interface MobileNavPresentation {
  labelKey: string;
  icon: ComponentType<{ className?: string; strokeWidth?: number }>;
}

/** Label + icon for every path `resolveMobileNav` (kernel) can place in the bottom bar or "More"
 * list. Keyed by path so the app shell can join the kernel's permission-resolved path list with how
 * each destination actually renders, without kernel knowing about icons or copy. */
export const mobileNavPresentation: Record<string, MobileNavPresentation> = {
  '/dashboard': { labelKey: 'workspace.mobile.nav.home', icon: House },
  '/appointments/requests': { labelKey: 'workspace.mobile.nav.requests', icon: ClipboardList },
  '/appointments': { labelKey: 'workspace.mobile.nav.schedule', icon: CalendarClock },
  '/queue': { labelKey: 'workspace.mobile.nav.queue', icon: ListTodo },
  '/patients': { labelKey: 'workspace.nav.patients', icon: ContactRound },
  '/prescriptions': { labelKey: 'workspace.nav.prescriptions', icon: FilePenLine },
  '/billing': { labelKey: 'workspace.nav.billing', icon: ReceiptText },
  '/reports': { labelKey: 'workspace.nav.reports', icon: ChartNoAxesCombined },
  '/staff': { labelKey: 'workspace.nav.staff', icon: UsersRound },
  '/roles': { labelKey: 'workspace.nav.roles', icon: ShieldCheck },
  '/subscription': { labelKey: 'workspace.nav.subscription', icon: CreditCard },
  '/audit': { labelKey: 'workspace.nav.audit', icon: ScrollText },
  '/prescription-configuration': { labelKey: 'workspace.nav.prescriptionConfiguration', icon: LibraryBig },
  '/settings': { labelKey: 'workspace.nav.settings', icon: Settings2 },
  '/account/settings': { labelKey: 'workspace.nav.mySettings', icon: Settings2 },
  '/mobile/more': { labelKey: 'workspace.nav.more', icon: PanelsTopLeft },
};

/** The in-shell drawers/sheets a quick action can open, as opposed to navigating somewhere. */
export type QuickActionType = 'patient' | 'appointment' | 'walkin';

interface QuickActionBase {
  labelKey: string;
  descriptionKey: string;
  icon: ComponentType<{ className?: string; strokeWidth?: number }>;
  tone: 'success' | 'info' | 'warning';
  permissions: string[];
  /** Entitlement gate, when the action needs one beyond its permissions. */
  feature?: string;
}

/**
 * A quick action either opens an in-shell surface (`actionType`) or navigates to a route (`route`) — never both.
 * Modelled as a discriminated union so the shells can narrow with `'actionType' in action` instead of casting.
 */
export type QuickAction =
  | (QuickActionBase & { actionType: QuickActionType; route?: never; state?: never })
  | (QuickActionBase & { route: string; state?: unknown; actionType?: never });

export const quickActions: readonly QuickAction[] = [
  { labelKey: 'workspace.mobile.quickActions.patient', descriptionKey: 'workspace.mobile.quickActions.patientDescription', icon: UserPlus, tone: 'success', permissions: ['patients.register'], feature: 'patientRegistration', actionType: 'patient' },
  { labelKey: 'workspace.mobile.quickActions.appointment', descriptionKey: 'workspace.mobile.quickActions.appointmentDescription', icon: CalendarPlus, tone: 'info', permissions: ['appointments.any.book'], actionType: 'appointment' },
  { labelKey: 'workspace.mobile.quickActions.invoice', descriptionKey: 'workspace.mobile.quickActions.invoiceDescription', icon: CreditCard, tone: 'success', permissions: ['billing.invoices.create'], feature: 'billing', route: '/billing/new' },
  { labelKey: 'workspace.mobile.quickActions.walkIn', descriptionKey: 'workspace.mobile.quickActions.walkInDescription', icon: Plus, tone: 'warning', permissions: ['queue.check_in'], actionType: 'walkin' },
];
